import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DxButtonModule, DxLoadPanelModule, DxTextAreaModule } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { EmailTemplateService, SupplierInvitationBrandAsset } from './email-template.service';

@Component({
  selector: 'app-email-template',
  templateUrl: './email-template.component.html',
  styleUrls: ['./email-template.component.css'],
  imports: [CommonModule, PageHeaderComponent, DxTextAreaModule, DxButtonModule, DxLoadPanelModule],
  standalone: true,
})
export class EmailTemplateComponent implements OnInit, OnDestroy {
  readonly maxLength = 10_000;
  readonly maxImageSize = 512 * 1024;
  readonly maxImageWidth = 1600;
  readonly maxImageHeight = 800;

  body = '';
  savedBody = '';
  isDefault = false;
  loading = true;
  saving = false;
  loadError = false;
  savedImage: SupplierInvitationBrandAsset = { configured: false };
  savedPreviewUrl: string | null = null;
  pendingImage: File | null = null;
  pendingPreviewUrl: string | null = null;
  pendingImageValidated = false;
  imageValidationError = '';
  imageApiError = '';
  templateApiError = '';
  imageLoading = false;
  imageLoadError = false;
  uploadingImage = false;
  removingImage = false;
  private destroyed = false;

  constructor(private emailTemplateService: EmailTemplateService) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.revokeUrl(this.savedPreviewUrl);
    this.revokeUrl(this.pendingPreviewUrl);
  }

  get characterCount(): number {
    return Array.from(this.body).length;
  }

  get isDirty(): boolean {
    return this.bodyDirty || this.pendingImage !== null;
  }

  get bodyDirty(): boolean {
    return this.body !== this.savedBody;
  }

  get isOverLimit(): boolean {
    return this.characterCount > this.maxLength;
  }

  get canSave(): boolean {
    return !this.loading && !this.loadError && !this.saving && this.bodyDirty && !this.isOverLimit;
  }

  get canSaveImage(): boolean {
    return this.pendingImage !== null
      && this.pendingImageValidated
      && !this.imageLoading
      && !this.uploadingImage
      && !this.removingImage;
  }

  get imageBusy(): boolean {
    return this.imageLoading || this.uploadingImage || this.removingImage;
  }

  onBodyChanged(event: { value?: string | null }): void {
    this.body = event?.value ?? '';
  }

  async retryLoad(): Promise<void> {
    await this.load();
  }

  async retryImageLoad(): Promise<void> {
    await this.loadImageMetadata();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectImage(input.files?.item(0) ?? null);
    input.value = '';
  }

  selectImage(file: File | null): void {
    if (file === null) {
      return;
    }

    this.imageApiError = '';
    this.clearPendingImage();
    if (file.type !== '' && !['image/png', 'image/jpeg'].includes(file.type)) {
      this.showImageValidationError('Choose a PNG or JPEG image.');
      return;
    }
    if (file.size > this.maxImageSize) {
      this.showImageValidationError('The image must be 512 KiB or smaller.');
      return;
    }

    this.imageLoadError = false;
    this.pendingImage = file;
    this.pendingPreviewUrl = URL.createObjectURL(file);
  }

  onPreviewLoaded(event: Event, previewUrl: string | null): void {
    if (previewUrl !== this.pendingPreviewUrl || this.pendingImage === null) {
      return;
    }

    const image = event.target as HTMLImageElement;
    if (image.naturalWidth > this.maxImageWidth || image.naturalHeight > this.maxImageHeight) {
      const dimensions = `${image.naturalWidth.toLocaleString('en-AU')} × ${image.naturalHeight.toLocaleString('en-AU')}`;
      this.clearPendingImage();
      this.showImageValidationError(
        `The selected image is ${dimensions} pixels. Choose an image no larger than ${this.maxImageWidth.toLocaleString('en-AU')} × ${this.maxImageHeight.toLocaleString('en-AU')} pixels.`,
      );
      return;
    }

    this.pendingImageValidated = true;
    this.imageValidationError = '';
  }

  discardSelectedImage(): void {
    this.clearPendingImage();
    this.imageApiError = '';
  }

  onPreviewError(previewUrl: string | null): void {
    if (previewUrl === this.pendingPreviewUrl && this.pendingImage !== null) {
      this.clearPendingImage();
      this.showImageValidationError('The selected file could not be read as a PNG or JPEG image.');
      return;
    }

    if (previewUrl !== this.savedPreviewUrl) {
      return;
    }

    this.imageLoadError = true;
    this.replaceSavedPreview(null);
  }

  async saveImage(): Promise<void> {
    if (!this.canSaveImage || this.pendingImage === null) {
      return;
    }

    this.imageApiError = '';
    this.uploadingImage = true;
    const submitted = this.pendingImage;
    try {
      const response = await firstValueFrom(this.emailTemplateService.uploadImage(submitted));
      if (this.destroyed) {
        return;
      }
      this.savedImage = response.data;
      if (this.pendingImage === submitted) {
        this.clearPendingImage();
      }
      await this.refreshSavedPreview();
      notify('Supplier invitation image saved.', 'success', 2500);
    } catch (error) {
      console.error('Failed to save the supplier invitation image');
      this.imageApiError = await this.apiErrorMessage(
        error,
        'Could not save the image. Your selection has been kept; please try again.',
      );
    } finally {
      this.uploadingImage = false;
    }
  }

  async removeImage(): Promise<void> {
    if (this.imageBusy || !this.savedImage.configured) {
      return;
    }

    this.imageApiError = '';
    this.removingImage = true;
    try {
      const response = await firstValueFrom(this.emailTemplateService.removeImage());
      if (this.destroyed) {
        return;
      }
      this.savedImage = response.data;
      this.replaceSavedPreview(null);
      notify('Supplier invitation image removed.', 'success', 2500);
    } catch (error) {
      console.error('Failed to remove the supplier invitation image');
      this.imageApiError = await this.apiErrorMessage(error, 'Could not remove the image; please try again.');
    } finally {
      this.removingImage = false;
    }
  }

  async save(): Promise<void> {
    if (this.isOverLimit) {
      notify(`Email body must be ${this.maxLength.toLocaleString('en-AU')} characters or fewer.`, 'error', 4000);
      return;
    }
    if (!this.canSave) {
      return;
    }

    this.templateApiError = '';
    this.saving = true;
    const submittedBody = this.body;
    try {
      const response = await firstValueFrom(this.emailTemplateService.update(submittedBody));
      if (this.destroyed) {
        return;
      }
      this.savedBody = response.data.body;
      if (this.body === submittedBody) {
        this.body = response.data.body;
      }
      this.isDefault = response.data.is_default;
      notify('Supplier invitation email template saved.', 'success', 2500);
    } catch (error) {
      console.error('Failed to save the supplier invitation email template');
      this.templateApiError = await this.apiErrorMessage(
        error,
        'Could not save the email template. Your edits have been kept; please try again.',
      );
    } finally {
      this.saving = false;
    }
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (this.saving || this.uploadingImage || this.removingImage) {
      return false;
    }
    if (!this.isDirty) {
      return true;
    }

    return confirm('You have unsaved email template or image changes. Leave without saving?', 'Unsaved changes');
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.isDirty || this.saving || this.uploadingImage || this.removingImage) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    this.templateApiError = '';
    try {
      const response = await firstValueFrom(this.emailTemplateService.get());
      if (this.destroyed) {
        return;
      }
      this.body = response.data.body;
      this.savedBody = response.data.body;
      this.isDefault = response.data.is_default;
      await this.loadImageMetadata();
    } catch (error) {
      console.error('Failed to load the supplier invitation email template');
      this.templateApiError = await this.apiErrorMessage(
        error,
        "Couldn't load the email template. Your saved template has not been changed.",
      );
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  private async loadImageMetadata(): Promise<void> {
    this.imageLoading = true;
    this.imageLoadError = false;
    this.imageApiError = '';
    try {
      const response = await firstValueFrom(this.emailTemplateService.getImage());
      if (this.destroyed) {
        return;
      }
      this.savedImage = response.data;
      if (response.data.configured) {
        await this.refreshSavedPreview();
      } else {
        this.replaceSavedPreview(null);
      }
    } catch (error) {
      console.error('Failed to load the supplier invitation image');
      this.imageApiError = await this.apiErrorMessage(error, 'The invitation image could not be loaded.');
      this.imageLoadError = true;
    } finally {
      this.imageLoading = false;
    }
  }

  private async refreshSavedPreview(): Promise<void> {
    try {
      const blob = await firstValueFrom(this.emailTemplateService.previewImage());
      if (this.destroyed) {
        return;
      }
      this.replaceSavedPreview(URL.createObjectURL(blob));
      this.imageLoadError = false;
    } catch (error) {
      console.error('Failed to load the supplier invitation image preview');
      this.replaceSavedPreview(null);
      this.imageApiError = await this.apiErrorMessage(error, 'The image preview could not be loaded.');
      this.imageLoadError = true;
    }
  }

  private clearPendingImage(): void {
    this.revokeUrl(this.pendingPreviewUrl);
    this.pendingPreviewUrl = null;
    this.pendingImage = null;
    this.pendingImageValidated = false;
    this.imageValidationError = '';
  }

  private showImageValidationError(message: string): void {
    this.imageValidationError = message;
  }

  private async apiErrorMessage(error: unknown, fallback: string): Promise<string> {
    let payload: unknown = error instanceof HttpErrorResponse
      ? error.error
      : (error as { error?: unknown } | null)?.error;

    if (payload instanceof Blob) {
      try {
        payload = JSON.parse(await payload.text());
      } catch {
        return fallback;
      }
    } else if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        return fallback;
      }
    }

    if (payload !== null && typeof payload === 'object') {
      const message = (payload as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim() !== '') {
        return message.trim();
      }
    }

    return fallback;
  }

  private replaceSavedPreview(url: string | null): void {
    this.revokeUrl(this.savedPreviewUrl);
    this.savedPreviewUrl = url;
  }

  private revokeUrl(url: string | null): void {
    if (url !== null) {
      URL.revokeObjectURL(url);
    }
  }
}
